/* Canvas — center column of the Rule Builder.
 *
 * Renders the four typed AST blocks in fixed vertical order:
 *   WHEN → IF → THEN → BECAUSE.
 * Drag-drop is scoped to the IF block (predicate composition); the
 * other three use plain typed selects / textareas — that's where
 * designers expect simple form controls, not free-form drag.
 *
 * Every change goes through `onChange(next)` so the parent route
 * owns state, persistence, validator + SPARQL preview.
 */

import { useEffect, useRef, useState, type DragEvent } from "react";
import { Wand2, GitBranch, Lightbulb, MessageSquareQuote, ArrowDown } from "lucide-react";
import type {
  CanvasNode,
  RuleEvent,
  Predicate,
  ExtendedTerm,
  Comparator,
  Action,
} from "../../../types/rule";
import type { PaletteDrag } from "./Palette";
import { cls } from "../../../lib/cls";

interface CanvasProps {
  nodes: CanvasNode[];
  onChange: (next: CanvasNode[]) => void;
}

const EVENTS: { id: RuleEvent; label: string }[] = [
  { id: "onSave",             label: "Every time the project is saved" },
  { id: "onBOQRowAdded",      label: "When a BOQ row is added" },
  { id: "onSkuChanged",       label: "When an SKU is added or changed" },
  { id: "onMilestoneSet",     label: "When a milestone is set or moved" },
  { id: "onApprovalDecided",  label: "When a client approval lands" },
  { id: "onBudgetChanged",    label: "When the budget cap or share moves" },
  { id: "onDeadlineChanged",  label: "When the handover date changes" },
];

const ACTIONS: Array<{ kind: Action["kind"]; label: string }> = [
  { kind: "flag",         label: "Flag it for review" },
  { kind: "suggest",      label: "Suggest a better choice" },
  { kind: "openApproval", label: "Open a client approval" },
  { kind: "substitute",   label: "Propose a substitute" },
];

function readDrag(e: DragEvent): PaletteDrag | null {
  const raw = e.dataTransfer.getData("application/x-foyrmind-rule-chip");
  if (!raw) return null;
  try { return JSON.parse(raw) as PaletteDrag; } catch { return null; }
}

/** Track palette-drag-in-progress globally so every TermSlot / OpSlot
 *  glows while the designer is mid-drag — "where can I drop this?"
 *  becomes self-evident without hover discovery. Listens at window
 *  level for dragstart / dragend on any element with the palette
 *  MIME type. CLAUDE.md §10: designer-obvious affordances. */
function usePaletteDragActive(): boolean {
  const [active, setActive] = useState(false);
  useEffect(() => {
    const onStart = (e: DragEvent<EventTarget> | globalThis.DragEvent): void => {
      const dt = (e as globalThis.DragEvent).dataTransfer;
      if (!dt) return;
      // Chrome doesn't populate getData() on dragenter elsewhere, but
      // types[] is readable. We only light up for our palette MIME.
      if (Array.from(dt.types).includes("application/x-foyrmind-rule-chip")) {
        setActive(true);
      }
    };
    const onEnd = (): void => setActive(false);
    window.addEventListener("dragstart", onStart as EventListener);
    window.addEventListener("dragend", onEnd);
    window.addEventListener("drop", onEnd);
    return () => {
      window.removeEventListener("dragstart", onStart as EventListener);
      window.removeEventListener("dragend", onEnd);
      window.removeEventListener("drop", onEnd);
    };
  }, []);
  return active;
}

function setIfPredicate(nodes: CanvasNode[], next: Predicate): CanvasNode[] {
  return nodes.map((n) =>
    n.kind === "IF" ? ({ ...n, data: { kind: "IF", predicate: next } } as CanvasNode) : n,
  );
}
function setWhen(nodes: CanvasNode[], event: RuleEvent): CanvasNode[] {
  return nodes.map((n) =>
    n.kind === "WHEN" ? ({ ...n, data: { kind: "WHEN", event } } as CanvasNode) : n,
  );
}
function setAction(nodes: CanvasNode[], action: Action): CanvasNode[] {
  return nodes.map((n) =>
    n.kind === "THEN" ? ({ ...n, data: { kind: "THEN", action } } as CanvasNode) : n,
  );
}
function setBecause(nodes: CanvasNode[], template: string): CanvasNode[] {
  return nodes.map((n) =>
    n.kind === "BECAUSE" ? ({ ...n, data: { kind: "BECAUSE", template } } as CanvasNode) : n,
  );
}

/* ─── helpers for mutating the inner Predicate AST. ────────────────── */

/* Seed Predicate — NOT an assumption. SKU.leadDays is the first
 * number-typed property listed in docs/ontology/codegen.json#fields
 * (verified at module load by ruleValidate's FIELD_MAP). Designers
 * change either side via drag-drop; the validator catches an empty
 * canvas and gates Promote before this seed ever ships. */
function defaultPredicate(): Predicate {
  return {
    kind: "cmp",
    lhs: { kind: "field", path: "SKU.leadDays", type: "number", unit: "days" },
    op: ">",
    rhs: { kind: "lit", type: "number", value: 60 },
  };
}

function dropOnTermSlot(p: Predicate, side: "lhs" | "rhs", drag: PaletteDrag): Predicate {
  if (p.kind !== "cmp") return p;
  if (drag.drag === "field") {
    const next: ExtendedTerm = drag.unit
      ? { kind: "field", path: drag.path, type: drag.type === "string" ? "string" : "number", unit: drag.unit }
      : { kind: "field", path: drag.path, type: drag.type === "string" ? "string" : "number" };
    return { ...p, [side]: next };
  }
  return p;
}

function setOp(p: Predicate, op: Comparator): Predicate {
  if (p.kind !== "cmp") return p;
  return { ...p, op };
}

function setLiteral(p: Predicate, side: "lhs" | "rhs", value: number | string): Predicate {
  if (p.kind !== "cmp") return p;
  const typ = typeof value === "number" ? "number" : "string";
  const lit: ExtendedTerm = { kind: "lit", type: typ, value };
  return { ...p, [side]: lit };
}

/* ─── Block wrappers ───────────────────────────────────────────────── */

function Block({
  eyebrow,
  title,
  icon,
  children,
}: {
  eyebrow: string;
  title: string;
  icon: JSX.Element;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="panel p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-7 h-7 rounded-lg bg-ink-950 text-cream grid place-items-center">{icon}</span>
        <div>
          <div className="eyebrow leading-none">{eyebrow}</div>
          <div className="text-[15px] font-semibold text-ink-950 leading-tight">{title}</div>
        </div>
      </div>
      {children}
    </div>
  );
}

function FlowArrow(): JSX.Element {
  return <div className="flex justify-center text-ink-300"><ArrowDown className="w-4 h-4" /></div>;
}

/* ─── Predicate slot — the drop target for fields / ops. ───────────── */

function TermSlot({
  term,
  onDrop,
  onEditLit,
  hint,
  dragActive,
}: {
  term: ExtendedTerm;
  onDrop: (d: PaletteDrag) => void;
  onEditLit: (v: number) => void;
  hint: string;
  dragActive: boolean;
}): JSX.Element {
  const dragRef = useRef<HTMLDivElement | null>(null);
  // Empty slot + drag-in-progress = strongest "drop here" cue.
  const isEmpty = term.kind === "lit" && term.value === "" || term.kind === "field" && !term.path;
  return (
    <div
      ref={dragRef}
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; dragRef.current?.classList.add("ring-2", "ring-foyr/60"); }}
      onDragLeave={() => dragRef.current?.classList.remove("ring-2", "ring-foyr/60")}
      onDrop={(e) => {
        e.preventDefault();
        dragRef.current?.classList.remove("ring-2", "ring-foyr/60");
        const d = readDrag(e);
        if (d) onDrop(d);
      }}
      className={cls(
        "px-3 py-2 rounded-lg border min-h-[40px] flex items-center gap-2 text-[13px] transition-all",
        dragActive
          ? (isEmpty
              ? "bg-foyr/10 border-foyr/50 border-dashed ring-2 ring-foyr/30 animate-pulse"
              : "bg-cream-dark border-foyr/40 border-dashed")
          : "bg-cream-dark border-border",
      )}
      title={hint}
    >
      {term.kind === "field" ? (
        <>
          <span className="font-mono text-sea">{term.path}</span>
          {term.unit ? <span className="text-[11px] text-ink-500">({term.unit})</span> : null}
        </>
      ) : term.kind === "lit" ? (
        term.type === "number" ? (
          <input
            type="number"
            value={Number(term.value)}
            onChange={(e) => onEditLit(Number(e.target.value))}
            className="w-24 bg-transparent border-none focus:outline-none font-mono text-ink-950 tabular"
          />
        ) : (
          <span className="font-mono text-ink-950">"{String(term.value)}"</span>
        )
      ) : (
        <span className="text-ink-500 italic">drop a field here</span>
      )}
    </div>
  );
}

function OpSlot({
  op,
  onDrop,
  dragActive,
}: {
  op: Comparator;
  onDrop: (d: PaletteDrag) => void;
  dragActive: boolean;
}): JSX.Element {
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; }}
      onDrop={(e) => { e.preventDefault(); const d = readDrag(e); if (d) onDrop(d); }}
      className={cls(
        "px-2.5 py-2 rounded-lg bg-foyr/10 text-foyr-deep text-[13px] font-mono transition-all",
        dragActive ? "border-2 border-foyr/60 border-dashed ring-2 ring-foyr/30" : "border border-foyr/30",
      )}
      title="Drop a comparator chip from the palette to change."
    >
      {op}
    </div>
  );
}

/* ─── The four blocks. ─────────────────────────────────────────────── */

export function Canvas({ nodes, onChange }: CanvasProps): JSX.Element {
  const whenNode = nodes.find((n) => n.kind === "WHEN");
  const ifNode   = nodes.find((n) => n.kind === "IF");
  const thenNode = nodes.find((n) => n.kind === "THEN");
  const becauseNode = nodes.find((n) => n.kind === "BECAUSE");

  const event = whenNode && whenNode.data.kind === "WHEN" ? whenNode.data.event : "onSave";
  const predicate = ifNode && ifNode.data.kind === "IF" ? ifNode.data.predicate : defaultPredicate();
  const action = thenNode && thenNode.data.kind === "THEN" ? thenNode.data.action : ({ kind: "flag", severity: "warn" } as Action);
  const template = becauseNode && becauseNode.data.kind === "BECAUSE" ? becauseNode.data.template : "";

  // IF block — only `cmp` is drag-drop today; other predicate kinds
  // (and/or/not/in_set/in_range) are reachable via library presets.
  const isCmp = predicate.kind === "cmp";
  // Global "drag in progress" — drives the pulse cue on every drop
  // target so designers never have to discover them by hover.
  const dragActive = usePaletteDragActive();

  return (
    <div className="space-y-2">
      <Block eyebrow="When" title="The moment this watches" icon={<Wand2 className="w-3.5 h-3.5" />}>
        <select
          value={event}
          onChange={(e) => onChange(setWhen(nodes, e.target.value as RuleEvent))}
          className="w-full px-3 py-2 rounded-lg border border-border bg-cream-dark text-[13px] focus:outline-none focus:border-foyr/60"
        >
          {EVENTS.map((ev) => <option key={ev.id} value={ev.id}>{ev.label}</option>)}
        </select>
      </Block>
      <FlowArrow />

      <Block eyebrow="If" title="The condition that must hold" icon={<GitBranch className="w-3.5 h-3.5" />}>
        {isCmp ? (
          <div className="flex items-stretch gap-2">
            <div className="flex-1">
              <div className="eyebrow mb-1">Left</div>
              <TermSlot
                term={predicate.lhs}
                hint="Drop a field chip on the left."
                dragActive={dragActive}
                onDrop={(d) => onChange(setIfPredicate(nodes, dropOnTermSlot(predicate, "lhs", d)))}
                onEditLit={(v) => onChange(setIfPredicate(nodes, setLiteral(predicate, "lhs", v)))}
              />
            </div>
            <div>
              <div className="eyebrow mb-1">Op</div>
              <OpSlot
                op={predicate.op}
                dragActive={dragActive}
                onDrop={(d) => {
                  if (d.drag === "op") onChange(setIfPredicate(nodes, setOp(predicate, d.op)));
                }}
              />
            </div>
            <div className="flex-1">
              <div className="eyebrow mb-1">Right</div>
              <TermSlot
                term={predicate.rhs}
                hint="Drop a field or edit a literal."
                dragActive={dragActive}
                onDrop={(d) => onChange(setIfPredicate(nodes, dropOnTermSlot(predicate, "rhs", d)))}
                onEditLit={(v) => onChange(setIfPredicate(nodes, setLiteral(predicate, "rhs", v)))}
              />
            </div>
          </div>
        ) : (
          <div className="px-3 py-2 rounded-lg bg-cream-dark border border-border text-[12px] text-ink-700">
            This rule uses a <span className="font-mono">{predicate.kind}</span> predicate.
            The drag-drop editor handles <span className="font-mono">cmp</span> today; other shapes (set / range / forbidden /
            and / or) are editable from the Rule Library or via the JSON snapshot below.
          </div>
        )}
      </Block>
      <FlowArrow />

      <Block eyebrow="Then" title="What the brain does" icon={<Lightbulb className="w-3.5 h-3.5" />}>
        <select
          value={action.kind}
          onChange={(e) => {
            const next = e.target.value as Action["kind"];
            const fresh: Action =
              next === "flag"         ? { kind: "flag", severity: "warn" } :
              next === "suggest"      ? { kind: "suggest", query: "review" } :
              next === "openApproval" ? { kind: "openApproval", role: "client" } :
              { kind: "substitute", criteria: { maxLeadDays: 21, minStyleMatch: 0.85, maxPriceDeltaPct: 0.15 } };
            onChange(setAction(nodes, fresh));
          }}
          className="w-full px-3 py-2 rounded-lg border border-border bg-cream-dark text-[13px] focus:outline-none focus:border-foyr/60"
        >
          {ACTIONS.map((a) => <option key={a.kind} value={a.kind}>{a.label}</option>)}
        </select>
        {action.kind === "flag" ? (
          <div className="mt-2 flex items-center gap-2 text-[12px]">
            <span className="eyebrow">Severity</span>
            {(["info", "warn", "block"] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onChange(setAction(nodes, { kind: "flag", severity: s }))}
                className={cls(
                  "pill border",
                  action.severity === s
                    ? (s === "block" ? "bg-foyr text-cream border-foyr" : s === "warn" ? "bg-foyr/10 border-foyr/40 text-foyr-deep" : "bg-cream-dark border-border text-ink-700")
                    : "bg-cream-dark border-border text-ink-500 hover:border-ink-300",
                )}
              >
                {s}
              </button>
            ))}
          </div>
        ) : null}
      </Block>
      <FlowArrow />

      <Block eyebrow="Because" title="The why the designer reads" icon={<MessageSquareQuote className="w-3.5 h-3.5" />}>
        <textarea
          value={template}
          onChange={(e) => onChange(setBecause(nodes, e.target.value))}
          rows={2}
          placeholder="One sentence the designer can quote in their client doc."
          className="w-full px-3 py-2 rounded-lg border border-border bg-cream-dark text-[13px] focus:outline-none focus:border-foyr/60 resize-none"
        />
      </Block>
    </div>
  );
}
